import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import * as React from "react";
import { useMutation } from "@tanstack/react-query";
import { requireAuth } from "~/lib/auth-guard";
import { useJobStore } from "~/stores/job-store";
import { orpc, client } from "~/lib/orpc-client";
import { useToast } from "@alchema/ui";
import * as m from "~/paraglide/messages";

export const Route = createFileRoute("/jobs/new")({
  beforeLoad: async () => { await requireAuth(); },
  component: NewJobPage,
});

type ExtractState = "idle" | "loading" | "success" | "error";

function NewJobPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [url, setUrl] = React.useState("");
  const [extractState, setExtractState] = React.useState<ExtractState>("idle");
  const [title, setTitle] = React.useState("");
  const [company, setCompany] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [savedJobId, setSavedJobId] = React.useState<string | null>(null);

  const createMutation = useMutation(orpc.job.create.mutationOptions());

  async function handleFetch() {
    if (!url.trim()) return;
    setExtractState("loading");
    setSavedJobId(null);
    try {
      const result = await client.enrichment.extractUrl({ jobUrl: url });
      const extractedTitle = result.title || "";
      let extractedCompany = result.company || "";
      const extractedDesc = result.description || "";

      // Derive company from hostname if extraction returned empty
      if (!extractedCompany) {
        try {
          const hostname = new URL(url).hostname.replace(/^www\./, "");
          const domain = hostname.split(".")[0];
          if (domain && domain.length > 2) {
            extractedCompany = domain.charAt(0).toUpperCase() + domain.slice(1);
          }
        } catch { /* ignore */ }
      }

      if (!extractedTitle && !extractedDesc) {
        setExtractState("error");
        toast(m.jobs_cors_fallback(), "error");
        return;
      }

      setTitle(extractedTitle);
      setCompany(extractedCompany);
      setDescription(extractedDesc);
      setExtractState("success");
      setPreviewOpen(true);

      // Auto-save immediately
      autoSave(extractedTitle, extractedCompany, extractedDesc);
    } catch (err) {
      console.error("[extractUrl] Failed:", err);
      setExtractState("error");
      toast(m.jobs_cors_fallback(), "error");
    }
  }

  function autoSave(jobTitle: string, jobCompany: string, jobDesc: string) {
    const saveTitle = jobTitle || "Untitled Job";
    const saveCompany = jobCompany || "Unknown";

    createMutation.mutate(
      { title: saveTitle, company: saveCompany, url: url || null, description: jobDesc, status: "saved" },
      {
        onSuccess: (data) => {
          setSavedJobId(data.id);
          useJobStore.setState((s) => ({
            jobs: [...s.jobs, {
              id: data.id,
              title: data.title ?? saveTitle,
              company: data.company ?? saveCompany,
              url: data.url ?? url ?? null,
              description: data.description ?? jobDesc,
              parsedRequirements: null,
              status: data.status ?? "saved",
              createdAt: data.createdAt ?? new Date().toISOString(),
              updatedAt: data.updatedAt ?? new Date().toISOString(),
            }],
          }));
          // Fire enrichment in background
          if (url) {
            client.enrichment.trigger({ url, jobId: data.id }).catch(() => {});
          }
          toast("Job saved — ready to tailor", "success");
        },
        onError: () => {
          // Fallback to local store
          const id = useJobStore.getState().addJob({
            title: saveTitle,
            company: saveCompany,
            url: url || null,
            description: jobDesc,
            parsedRequirements: null,
            status: "saved",
          });
          setSavedJobId(id);
          toast("Job saved locally", "success");
        },
      },
    );
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && url.trim() && extractState !== "loading") {
      e.preventDefault();
      handleFetch();
    }
  }

  return (
    <div className="mx-auto max-w-2xl animate-fade-up">
      {/* Breadcrumb */}
      <div className="mb-6 flex items-center gap-2 text-sm text-[var(--text-muted)]">
        <Link to="/jobs" className="hover:text-[var(--text-secondary)] transition-colors">
          {m.jobs_title()}
        </Link>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6" />
        </svg>
        <span className="text-[var(--text)]">{m.jobs_new_job()}</span>
      </div>

      <div className="page-header">
        <h1 className="page-title">{m.jobs_add_job()}</h1>
        <p className="mt-1 text-sm text-[var(--text-muted)]">
          Paste a job URL and we'll extract the details instantly.
        </p>
      </div>

      {/* URL Input */}
      <div className="card card-sky mt-6 p-6 animate-fade-up stagger-1">
        <div className="flex items-center gap-2 mb-4">
          <div className="flex h-8 w-8 items-center justify-center rounded-[10px] bg-[var(--accent)] text-white">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
            </svg>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-[var(--text)]">{m.jobs_import_from_url()}</h2>
            <p className="text-xs text-[var(--text-muted)]">{m.jobs_import_url_desc()}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <input
            value={url}
            onChange={(e) => { setUrl(e.target.value); if (extractState !== "idle") setExtractState("idle"); }}
            onKeyDown={handleKeyDown}
            placeholder={m.jobs_placeholder_url()}
            className="input flex-1"
            disabled={extractState === "loading"}
          />
          <button
            onClick={handleFetch}
            disabled={extractState === "loading" || !url.trim()}
            className="btn btn-accent"
          >
            {extractState === "loading" ? (
              <span className="flex items-center gap-1.5">
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeLinecap="round" />
                </svg>
                {m.jobs_fetching()}
              </span>
            ) : (
              m.jobs_fetch()
            )}
          </button>
        </div>
      </div>

      {/* Extraction Result */}
      {extractState === "success" && (
        <div className="mt-4 animate-fade-up stagger-2">
          {/* Job Header */}
          <div className="card p-6">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-[var(--success-subtle)] text-[var(--success)]">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-semibold text-[var(--text)] leading-tight">{title || "Untitled Job"}</h2>
                <p className="mt-0.5 text-sm text-[var(--text-muted)]">{company}</p>
                {url && (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-flex items-center gap-1 text-xs text-[var(--accent)] hover:underline"
                  >
                    {m.jobs_view_original_posting()}
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  </a>
                )}
              </div>
              {createMutation.isPending && (
                <span className="text-xs text-[var(--text-muted)] flex items-center gap-1">
                  <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeLinecap="round" />
                  </svg>
                  Saving...
                </span>
              )}
            </div>

            {/* Collapsible description preview */}
            {description && (
              <div className="mt-4">
                <button
                  onClick={() => setPreviewOpen(!previewOpen)}
                  className="flex items-center gap-1.5 text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--text)] transition-colors"
                >
                  <svg
                    width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round"
                    className={`transition-transform duration-200 ${previewOpen ? "rotate-90" : ""}`}
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                  {previewOpen ? "Hide job description" : "Show job description"}
                </button>
                {previewOpen && (
                  <div className="mt-3 max-h-64 overflow-y-auto rounded-[10px] border border-[var(--border-subtle)] bg-[var(--bg-secondary)] p-4">
                    <div className="text-xs text-[var(--text-secondary)] leading-relaxed whitespace-pre-wrap">
                      {description.slice(0, 3000)}
                      {description.length > 3000 && (
                        <span className="text-[var(--text-muted)]">... ({Math.round(description.length / 1000)}k chars)</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Primary CTA */}
          <div className="mt-6 flex flex-col items-center gap-3 animate-fade-up stagger-3">
            <button
              onClick={() => {
                if (savedJobId) {
                  navigate({ to: "/jobs/$jobId", params: { jobId: savedJobId } });
                }
              }}
              disabled={!savedJobId || createMutation.isPending}
              className="btn btn-primary w-full py-3.5 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <span className="flex items-center justify-center gap-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14.5 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V7.5L14.5 2z" />
                  <polyline points="14 2 14 8 20 8" />
                  <path d="M12 18v-6" />
                  <path d="M9 15l3-3 3 3" />
                </svg>
                {m.jobs_tailor_resume()}
              </span>
            </button>
            <button
              onClick={() => navigate({ to: "/jobs" })}
              className="text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
            >
              or go back to job board
            </button>
          </div>
        </div>
      )}

      {/* Error state */}
      {extractState === "error" && (
        <div className="card mt-4 p-6 text-center animate-fade-up stagger-2">
          <div className="flex h-12 w-12 mx-auto items-center justify-center rounded-full bg-[var(--error-subtle)]">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--error)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <p className="mt-3 text-sm font-medium text-[var(--text)]">Couldn't extract job details</p>
          <p className="mt-1 text-xs text-[var(--text-muted)]">
            The site may block automated access. Try a different URL or paste the job description manually.
          </p>
          <button
            onClick={handleFetch}
            className="btn btn-secondary btn-sm mt-4"
          >
            Retry
          </button>
        </div>
      )}

      {/* Idle state hint */}
      {extractState === "idle" && (
        <div className="mt-8 text-center text-sm text-[var(--text-muted)] animate-fade-up stagger-2">
          <p>Paste a job URL above to get started.</p>
          <p className="mt-1 text-xs">Works with LinkedIn, StepStone, Indeed, and most job boards.</p>
        </div>
      )}
    </div>
  );
}
